/**
 * Similarity Engine — Jaccard-based contact similarity using leadSignals.
 *
 * Weights: tags 40%, offerType 25%, sizeProxy 15%, industry 15%, b2b_b2c 5%.
 * Pure computation — no Firebase imports, no side-effects.
 */

export const SIMILARITY_WEIGHTS = {
  tags:      0.40,
  offerType: 0.25,
  sizeProxy: 0.15,
  industry:  0.15,
  b2b_b2c:   0.05,
};

const MIN_SCORE_DEFAULT = 15;
const TOP_N_DEFAULT     = 10;

// ---------------------------------------------------------------------------
// Core math
// ---------------------------------------------------------------------------

export function jaccardIndex(arrA, arrB) {
  if (!arrA?.length || !arrB?.length) return 0;

  const a = new Set(arrA.map(t => t.toLowerCase()));
  const b = new Set(arrB.map(t => t.toLowerCase()));

  let intersection = 0;
  for (const item of a) {
    if (b.has(item)) intersection++;
  }

  const unionSize = new Set([...a, ...b]).size;
  return unionSize === 0 ? 0 : intersection / unionSize;
}

function exactMatch(valA, valB) {
  if (!valA || !valB || valA === 'unknown' || valB === 'unknown') return 0;
  return valA.toLowerCase() === valB.toLowerCase() ? 1 : 0;
}

function getIntersection(arrA, arrB) {
  const setB = new Set((arrB || []).map(t => t.toLowerCase()));
  return (arrA || []).filter(t => setB.has(t.toLowerCase()));
}

// ---------------------------------------------------------------------------
// Pairwise similarity
// ---------------------------------------------------------------------------

export function computeContactSimilarity(contactA, contactB) {
  const sigA = contactA.leadSignals || {};
  const sigB = contactB.leadSignals || {};

  const tagsScore     = jaccardIndex(sigA.tags || [], sigB.tags || []);
  const offerScore    = exactMatch(sigA.offerType, sigB.offerType);
  const sizeScore     = exactMatch(sigA.sizeProxy, sigB.sizeProxy);
  const industryScore = exactMatch(sigA.industry, sigB.industry);
  const b2bScore      = exactMatch(sigA.b2b_b2c, sigB.b2b_b2c);

  const score =
    SIMILARITY_WEIGHTS.tags      * tagsScore +
    SIMILARITY_WEIGHTS.offerType * offerScore +
    SIMILARITY_WEIGHTS.sizeProxy * sizeScore +
    SIMILARITY_WEIGHTS.industry  * industryScore +
    SIMILARITY_WEIGHTS.b2b_b2c   * b2bScore;

  return {
    score: Math.round(score * 100),
    breakdown: {
      tags:      Math.round(tagsScore * 100),
      offerType: offerScore === 1,
      sizeProxy: sizeScore === 1,
      industry:  industryScore === 1,
      b2b_b2c:   b2bScore === 1,
    },
    matchedTags: getIntersection(sigA.tags, sigB.tags),
  };
}

// ---------------------------------------------------------------------------
// Batch: find top-N similar contacts from a candidate array
// ---------------------------------------------------------------------------

export function findSimilarContacts(targetContact, allContacts, options = {}) {
  const { topN = TOP_N_DEFAULT, minScore = MIN_SCORE_DEFAULT } = options;

  if (!targetContact?.leadSignals) return [];

  const results = [];

  for (const contact of allContacts) {
    if (contact.id === targetContact.id) continue;
    if (!contact.leadSignals) continue;

    try {
      const similarity = computeContactSimilarity(targetContact, contact);
      if (similarity.score >= minScore) {
        results.push({ contact, ...similarity });
      }
    } catch (_) {
      // fire-and-forget: never crash the CRM
    }
  }

  results.sort((a, b) => b.score - a.score);
  return results.slice(0, topN);
}
